/**
 * Loans Panel — data table with filtering, sorting, row click → detail panel,
 * and add loan button.
 */

import { useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import {
  deriveLoanBalanceEstimates,
  resolveRocapValuationIndex,
} from "@cvx/jobs/summarization/lib/rocapValuation";
import { Badge, type BadgeColor } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import {
  Loader2,
  Landmark,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Plus,
  ChevronRight,
} from "lucide-react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  type SortingState,
  type ColumnDef,
} from "@tanstack/react-table";
import { cn } from "@/utils/misc";
import { directorDisclosureLabel } from "@/lib/director-disclosure";
import type { LoanEntryUI } from "./types";
import { LoanDetailPanel } from "./LoanDetailPanel";
import {
  useNewOutputBoundary,
  isNewOutput,
} from "../../../_shared/use-new-output-boundary";
import { NewOutputBadge } from "../../../_shared/new-output-badge";
import {
  useLinkCounts,
  useLinkedRecordStack,
} from "../shared-panels/linkShared";
import { LinkIndicator } from "../shared-panels/LinkIndicator";
import { SecondaryDetailPanel } from "../shared-panels/SecondaryDetailPanel";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const fmt = (v: number) =>
  `$${v.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso?: string) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-AU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

const DIRECTION_CONFIG: Record<
  string,
  { label: string; color: string; badgeColor: BadgeColor }
> = {
  Loan_Granted: {
    label: "Granted",
    color: "text-red-700 dark:text-red-400",
    badgeColor: "red",
  },
  Loan_Taken: {
    label: "Taken",
    color: "text-emerald-700 dark:text-emerald-400",
    badgeColor: "emerald",
  },
};

const AGREEMENT_CONFIG: Record<
  string,
  { label: string; badgeColor: BadgeColor }
> = {
  Formal_Contract: { label: "Formal", badgeColor: "emerald" },
  Invoice_Only: { label: "Invoice", badgeColor: "amber" },
  No_Documentation: { label: "No docs", badgeColor: "red" },
};

const SECURITY_CONFIG: Record<
  string,
  { label: string; badgeColor: BadgeColor }
> = {
  Unsecured: { label: "Unsecured", badgeColor: "red" },
  PPSR_Registered: { label: "PPSR", badgeColor: "blue" },
  Mortgage_Registered: { label: "Mortgage", badgeColor: "emerald" },
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "granted", label: "Granted" },
  { key: "taken", label: "Taken" },
  { key: "unsecured", label: "Unsecured" },
] as const;

// ---------------------------------------------------------------------------
// Columns
// ---------------------------------------------------------------------------

function makeColumns(
  linkCounts: ReturnType<typeof useLinkCounts>,
  newSince: number | null,
  /** Director-disclosure column header for the appointment type. */
  disclosureShort: string,
): ColumnDef<LoanEntryUI>[] {
  return [
    {
      accessorKey: "date",
      header: "Date",
      cell: ({ getValue }) => (
        <span className="text-xs tabular-nums">
          {fmtDate(getValue<string>())}
        </span>
      ),
    },
    {
      accessorKey: "counterpartyName",
      header: "Counterparty",
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">
            {row.original.counterpartyName}
          </span>
          {isNewOutput(row.original._creationTime, newSince) && (
            <NewOutputBadge />
          )}
          <LinkIndicator
            count={linkCounts.byLinkedEntity.get(row.original._id) ?? 0}
          />
        </div>
      ),
    },
    {
      accessorKey: "amount",
      header: "Total Amount",
      cell: ({ row }) => {
        const dir = DIRECTION_CONFIG[row.original.direction];
        return (
          <span
            className={cn(
              "font-mono text-sm font-semibold tabular-nums",
              dir?.color,
            )}
          >
            {fmt(row.original.amount)}
          </span>
        );
      },
    },
    {
      accessorKey: "outstandingBalance",
      header: "Outstanding",
      cell: ({ getValue }) => (
        <span className="font-mono text-sm font-semibold tabular-nums">
          {fmt(getValue<number>())}
        </span>
      ),
    },
    {
      id: "rocap",
      header: disclosureShort,
      cell: ({ row }) => {
        const estimates = deriveLoanBalanceEstimates(row.original);
        const index = resolveRocapValuationIndex(estimates, undefined);
        const value = index != null ? estimates[index]?.value : null;
        return value != null ? (
          <span className="font-mono text-sm tabular-nums">{fmt(value)}</span>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        );
      },
    },
    {
      accessorKey: "direction",
      header: "Direction",
      cell: ({ getValue }) => {
        const dir = DIRECTION_CONFIG[getValue<string>()];
        return dir ? (
          <Badge color={dir.badgeColor} variant="soft" size="sm">
            {dir.label}
          </Badge>
        ) : null;
      },
    },
    {
      accessorKey: "agreementStatus",
      header: "Agreement",
      cell: ({ getValue }) => {
        const cfg = AGREEMENT_CONFIG[getValue<string>()];
        return cfg ? (
          <Badge color={cfg.badgeColor} variant="soft" size="sm">
            {cfg.label}
          </Badge>
        ) : null;
      },
    },
    {
      accessorKey: "securityStatus",
      header: "Security",
      cell: ({ getValue }) => {
        const cfg = SECURITY_CONFIG[getValue<string>()];
        return cfg ? (
          <Badge color={cfg.badgeColor} variant="soft" size="sm">
            {cfg.label}
          </Badge>
        ) : null;
      },
    },
    {
      id: "chevron",
      header: "",
      cell: () => <ChevronRight className="h-4 w-4 text-muted-foreground/40" />,
    },
  ];
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function LoansPanel({
  jobId,
  taskId,
  isRunning,
  isViewer,
  compact = false,
}: {
  jobId: Id<"jobs">;
  taskId: Id<"summarizationTasks">;
  isRunning: boolean;
  isViewer?: boolean;
  /**
   * When `true`, hides the three header stats (Granted / Taken /
   * Unsecured Exposure) and the "Add Loan" button so the panel renders
   * as table-only. Used by the Overview (Plan) page.
   */
  compact?: boolean;
}) {
  const [sorting, setSorting] = useState<SortingState>([
    { id: "date", desc: false },
  ]);
  const [filter, setFilter] = useState<string>("all");
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);

  const linkCounts = useLinkCounts(jobId);

  // Stack of side-by-side linked-record panels so the user can chase a chain
  // of related records (Loan → Creditor → SI ...) without losing context.
  const linkedStack = useLinkedRecordStack(
    selectedEntryId ? { type: "loan", id: selectedEntryId } : null,
  );
  const newSince = useNewOutputBoundary(jobId, "sum_loans");

  // Director-disclosure label (ROCAP / SOA / …) for the job's appointment type.
  const { data: job } = useQuery(
    convexQuery(api.jobs.queries.getJob, { jobId }),
  );
  const disclosure = directorDisclosureLabel(job?.appointmentType);

  const columns = useMemo(
    () => makeColumns(linkCounts, newSince, disclosure.short),
    [linkCounts, newSince, disclosure.short],
  );

  const { data: allEntries } = useQuery(
    convexQuery(api.jobs.summarization.publicQueries.listLoanEntriesForJob, {
      jobId,
    }),
  );

  const addLoan = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.loans.publicMutations.addManualLoan,
    ),
  });

  const entries = useMemo<LoanEntryUI[]>(() => allEntries ?? [], [allEntries]);

  const filtered = useMemo(() => {
    if (filter === "granted")
      return entries.filter((e) => e.direction === "Loan_Granted");
    if (filter === "taken")
      return entries.filter((e) => e.direction === "Loan_Taken");
    if (filter === "unsecured")
      return entries.filter((e) => e.securityStatus === "Unsecured");
    return entries;
  }, [entries, filter]);

  const counts = useMemo(
    () => ({
      all: entries.length,
      granted: entries.filter((e) => e.direction === "Loan_Granted").length,
      taken: entries.filter((e) => e.direction === "Loan_Taken").length,
      unsecured: entries.filter((e) => e.securityStatus === "Unsecured").length,
    }),
    [entries],
  );

  const totalGranted = useMemo(
    () =>
      entries
        .filter((e) => e.direction === "Loan_Granted")
        .reduce((s, e) => s + e.outstandingBalance, 0),
    [entries],
  );
  const totalTaken = useMemo(
    () =>
      entries
        .filter((e) => e.direction === "Loan_Taken")
        .reduce((s, e) => s + e.outstandingBalance, 0),
    [entries],
  );
  const unsecuredGranted = useMemo(
    () =>
      entries.filter(
        (e) =>
          e.securityStatus === "Unsecured" && e.direction === "Loan_Granted",
      ).length,
    [entries],
  );
  const unsecuredTaken = useMemo(
    () =>
      entries.filter(
        (e) => e.securityStatus === "Unsecured" && e.direction === "Loan_Taken",
      ).length,
    [entries],
  );
  const securedCount = useMemo(
    () => entries.filter((e) => e.securityStatus !== "Unsecured").length,
    [entries],
  );
  const securedGranted = useMemo(
    () =>
      entries.filter(
        (e) =>
          e.securityStatus !== "Unsecured" && e.direction === "Loan_Granted",
      ).length,
    [entries],
  );
  const securedTaken = useMemo(
    () =>
      entries.filter(
        (e) => e.securityStatus !== "Unsecured" && e.direction === "Loan_Taken",
      ).length,
    [entries],
  );

  const selectedEntry = useMemo(
    () => entries.find((e) => e._id === selectedEntryId) ?? null,
    [entries, selectedEntryId],
  );

  const table = useReactTable({
    data: filtered,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (entries.length === 0) {
    if (isRunning) {
      return (
        <div className="flex flex-col items-center gap-3 py-16">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
          <p className="text-sm text-muted-foreground">Extracting loans...</p>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center gap-4 py-16">
        <div className="rounded-full bg-muted p-3">
          <Landmark className="h-6 w-6 text-muted-foreground" />
        </div>
        <p className="text-sm text-muted-foreground">No loan entries yet.</p>
        {!isViewer && (
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() =>
              addLoan.mutate(
                { jobId, taskId, counterpartyName: "New Loan" },
                { onSuccess: (id) => setSelectedEntryId(id as string) },
              )
            }
          >
            <Plus className="h-3.5 w-3.5" /> Add Loan
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {/* Card */}
      <div className="overflow-hidden rounded-xl border border-border bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] transition-shadow duration-200 hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)]">
        {/* Header — Stats + Add button (hidden in compact mode) */}
        {!compact && (
          <div className="flex items-start justify-between px-5 pb-4 pt-5">
            <div className="flex gap-8">
              <div>
                <p className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Granted (outstanding amounts)
                </p>
                <p className="text-2xl font-bold tabular-nums text-red-700 dark:text-red-400">
                  {fmt(totalGranted)}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {counts.granted} loan{counts.granted !== 1 ? "s" : ""}{" "}
                  outstanding
                </p>
              </div>
              <div>
                <p className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Taken (outstanding amounts)
                </p>
                <p className="text-2xl font-bold tabular-nums text-emerald-700 dark:text-emerald-400">
                  {fmt(totalTaken)}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {counts.taken} loan{counts.taken !== 1 ? "s" : ""} outstanding
                </p>
              </div>
              <div>
                <p className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  No. of Secured Loans
                </p>
                <p className="text-2xl font-bold tabular-nums text-foreground">
                  {securedCount}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {securedGranted > 0 || securedTaken > 0
                    ? `${securedGranted} granted, ${securedTaken} taken`
                    : "No secured loans identified"}
                </p>
              </div>
              <div>
                <p className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  No. of Unsecured Loans
                </p>
                <p className="text-2xl font-bold tabular-nums text-foreground">
                  {counts.unsecured}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {unsecuredGranted > 0 || unsecuredTaken > 0
                    ? `${unsecuredGranted} granted, ${unsecuredTaken} taken`
                    : "No unsecured loans identified"}
                </p>
              </div>
            </div>
            {!isViewer && (
              <Button
                size="sm"
                className="gap-1.5"
                disabled={addLoan.isPending}
                onClick={() =>
                  addLoan.mutate(
                    { jobId, taskId, counterpartyName: "New Loan" },
                    { onSuccess: (id) => setSelectedEntryId(id as string) },
                  )
                }
              >
                <Plus className="h-3.5 w-3.5" /> Add Loan
              </Button>
            )}
          </div>
        )}

        {/* Filter tabs */}
        <div className="scrollbar-none flex items-center gap-1 overflow-x-auto border-b border-border px-5">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={cn(
                "-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors",
                filter === f.key
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {f.label}
              <span
                className={cn(
                  "ml-1.5 text-xs tabular-nums",
                  filter === f.key
                    ? "text-muted-foreground"
                    : "text-muted-foreground/50",
                )}
              >
                {counts[f.key]}
              </span>
            </button>
          ))}
        </div>

        {/* Data table */}
        <div className="min-w-0">
          <div className="max-h-[calc(100vh-460px)] overflow-auto">
            <Table className="w-full min-w-[900px]">
              <TableHeader>
                {table.getHeaderGroups().map((hg) => (
                  <TableRow
                    key={hg.id}
                    className="bg-muted/30 hover:bg-transparent"
                  >
                    {hg.headers.map((header) => (
                      <TableHead
                        key={header.id}
                        className={cn(
                          "h-10 select-none whitespace-nowrap text-[10px] font-semibold uppercase tracking-wider text-muted-foreground",
                          header.column.getCanSort() && "cursor-pointer",
                          (header.id === "amount" ||
                            header.id === "outstandingBalance") &&
                            "text-right",
                        )}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <span className="inline-flex items-center gap-1">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                          {header.column.getCanSort() &&
                            (header.column.getIsSorted() === "asc" ? (
                              <ArrowUp className="h-3 w-3" />
                            ) : header.column.getIsSorted() === "desc" ? (
                              <ArrowDown className="h-3 w-3" />
                            ) : (
                              <ArrowUpDown className="h-3 w-3 text-muted-foreground/30" />
                            ))}
                        </span>
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    className={cn(
                      "cursor-pointer transition-colors",
                      selectedEntryId === row.original._id
                        ? "hover:bg-primary/8 bg-primary/5"
                        : "hover:bg-muted/50",
                    )}
                    onClick={() => setSelectedEntryId(row.original._id)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={cn(
                          "px-4 py-2.5",
                          (cell.column.id === "amount" ||
                            cell.column.id === "outstandingBalance") &&
                            "text-right",
                        )}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Footer hint */}
        <div className="border-t border-border px-5 py-3">
          <p className="text-center text-xs text-muted-foreground">
            Click any row to view details, edit, and see history
          </p>
        </div>
      </div>

      {/* Detail panel */}
      <LoanDetailPanel
        entry={selectedEntry}
        disclosure={disclosure}
        open={selectedEntry !== null}
        onClose={() => {
          setSelectedEntryId(null);
          linkedStack.closeAll();
        }}
        onDeleted={() => {
          setSelectedEntryId(null);
          linkedStack.closeAll();
        }}
        isViewer={isViewer}
        onOpenLinked={linkedStack.open}
      />

      {/* Stack of side-by-side linked records */}
      <SecondaryDetailPanel
        jobId={jobId}
        stack={linkedStack.stack}
        onCloseAt={linkedStack.closeAt}
        onOpenLinked={linkedStack.open}
        isViewer={isViewer}
      />
    </div>
  );
}
